import { useTheme } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { CommandPalette } from './CommandPalette';
import { MobileNav, Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

import { AssistantPanel } from '@/features/assistant/components/AssistantPanel';
import { LastSignInNotice } from '@/features/my-account/components/LastSignInNotice';
import { useActiveShop } from '@/features/shops/use-active-shop';
import { useLiveUpdates } from '@/lib/live-updates';

/**
 * `useTheme()` and `useActiveShop()` are each called exactly once here and
 * passed down, rather than separately in every consumer. `@ecom/ui-kit`'s
 * theme hook has no cross-instance sync (confirmed live in Lot 1: two
 * independent instances desynced the moment one of them toggled). The same
 * risk applies to `useActiveShop()` for a different reason: it owns an
 * effect that auto-selects a shop, and it queries `/auth/me/memberships` —
 * a second instance would refetch redundantly and could, in principle,
 * race its own auto-select effect against a sibling's.
 */
export function AppShell(): ReactElement {
  const theme = useTheme();
  const shop = useActiveShop();
  // Migration 0098 — one live stream for the whole shell: every screen refreshes by itself.
  const liveStatus = useLiveUpdates();
  // Phones and tablets: the sidebar's links open from the top bar's menu button.
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="flex h-dvh w-full bg-ground">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          theme={theme}
          shop={shop}
          liveStatus={liveStatus}
          navOpen={navOpen}
          onOpenNav={() => {
            setNavOpen(true);
          }}
          onOpenPalette={() =>
            document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))
          }
        />
        <LastSignInNotice />
        {/* Tighter padding on a phone. The bottom space lets the last button scroll clear of the floating assistant
            button and of the iPhone home bar. */}
        <main className="min-w-0 flex-1 overflow-y-auto px-3 pt-4 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:px-6 sm:pt-6">
          <Outlet />
        </main>
      </div>
      <MobileNav
        open={navOpen}
        onClose={() => {
          setNavOpen(false);
        }}
      />
      <CommandPalette theme={theme} />
      <AssistantPanel />
    </div>
  );
}
